import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { database } from '../../Firebase';
import { ref, onValue } from 'firebase/database';

const TaxBillReport = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      const recordRef = ref(database, `Grampanchayat/Gramniddhi/NewMilakhatRecords/${id}`);
      onValue(recordRef, (snapshot) => {
        const data = snapshot.val();
        setRecord(data);
        setLoading(false);
      });
    }
  }, [id]);

  if (loading) return <div className="p-4 text-center">Loading...</div>;
  if (!record) return <div className="p-4 text-center text-red-500">Record not found</div>;

  const handlePrint = () => {
    window.print();
  };

  const toMarathiNums = (n) => {
    if (n === undefined || n === null) return '';
    const marathiMap = { '0': '०', '1': '१', '2': '२', '3': '३', '4': '४', '5': '५', '6': '६', '7': '७', '8': '८', '9': '९' };
    return n.toString().split('').map(char => marathiMap[char] || char).join('');
  };

  // Helper: Number to Marathi Words (Enhanced stub)
  const numToMarathiWords = (n) => {
    // Ideally this would be a full function, for now utilizing the number directly or a placeholder
    // User asked for "Same to same", so dynamic data is key.
    return `${n} (अक्षरी)`;
  };

  // --- Data Prep ---
  const taxes = record.taxData || {};
  const houseTax = taxes.houseTax || { current: 0, previous: 0 };
  const healthTax = taxes.healthTax || { current: 0, previous: 0 };
  const lightTax = taxes.electricityTax || { current: 0, previous: 0 };

  // Amounts
  const hPre = parseInt(houseTax.previous) || 0;
  const hCur = parseInt(houseTax.current) || 0;
  const hTot = hPre + hCur;

  const lPre = parseInt(lightTax.previous) || 0;
  const lCur = parseInt(lightTax.current) || 0;
  const lTot = lPre + lCur;

  const hlPre = parseInt(healthTax.previous) || 0;
  const hlCur = parseInt(healthTax.current) || 0;
  const hlTot = hlPre + hlCur;

  const noticeFee = parseInt(record.noticeFee) || 0;
  const warrantFee = parseInt(record.warrantFee) || 0;
  const penalty = parseInt(record.penalty) || 10; // "घरपट्टी थकबाकी दंड" - Placeholder 10 as per image if not dynamic

  const totalArrears = hPre + lPre + hlPre;
  const totalCurrent = hCur + lCur + hlCur;
  const subTotal = hTot + lTot + hlTot + noticeFee + warrantFee + penalty;

  const discount = Math.round(subTotal * 0.05); // Example 5% discount
  const finalTotal = subTotal - discount;

  // Dates
  const billDate = "01/04/2025";
  const dueDate = "16/04/2025"; // 15 days later
  const discountDate = "30/09/25";
  const yearRange = "2025 ते 2026";


  const BillTemplate = ({ copyTitle }) => (
    <div className="border border-black mb-8 relative text-black" style={{ fontFamily: '"Times New Roman", serif' }}>

      {/* Top Row: Village & Header */}
      <div className="flex border-b border-black">
        {/* Left: Village Name */}
        <div className="w-[65%] text-center border-r border-black flex flex-col justify-center py-2">
          <h2 className="font-bold text-xl">ग्रामपंचायत इनाम धामणी ,</h2>
          <h3 className="font-bold text-lg">तालुका मिरज , जिल्हा सांगली.</h3>
        </div>

        {/* Right: Form Name */}
        <div className="w-[35%] text-center p-1">
          <p className="text-[10px] leading-tight">मुंबई ग्रामपंचायत अधिनियम, १९५८ नुसार</p>
          <p className="text-[10px] leading-tight">कायदा कलम १२९ पोट कलम(१)</p>
          <h1 className="text-xl font-bold mt-1">नमुना ९(क) कराची मागणी</h1>
          <p className="font-bold text-sm mt-1">सन {yearRange}</p>
        </div>
      </div>

      {/* Row 2: Owner Name */}
      <div className="flex justify-between border-b border-black px-2 py-1 items-end">
        <p className="font-semibold text-sm w-full relative">
          श्री./सौ. &nbsp; <span className="font-normal">{record.mainOwner}</span>
          <span className="absolute right-0">यांस</span>
        </p>
      </div>

      {/* Row 3: Occupier & Bill No */}
      <div className="flex border-b border-black">
        <div className="w-[65%] border-r border-black p-1 text-sm">
          भोगवटदार : {record.occupierName || '-----------'}
        </div>
        <div className="w-[35%] p-1 text-sm font-bold">
          बिल नंबर : {id ? id.toString().slice(-4) : '0000'}
        </div>
      </div>

      {/* Main Split: Table & Instructions */}
      <div className="flex h-auto">
        {/* Left Side: Tax Table */}
        <div className="w-[65%] border-r border-black flex flex-col">

          {/* Table Header */}
          <div className="flex border-b border-black text-center text-sm font-bold bg-white h-auto">
            <div className="w-[30%] border-r border-black flex items-center justify-center p-1">कराचे नाव</div>
            <div className="w-[70%]">
              <div className="border-b border-black p-1">वसुल पात्र रकमा</div>
              <div className="flex">
                <div className="w-1/3 border-r border-black p-1">थकबाकी</div>
                <div className="w-1/3 border-r border-black p-1">चालू वर्ष</div>
                <div className="w-1/3 p-1">एकुण</div>
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className="flex-grow text-sm">
            {/* House Tax */}
            <div className="flex text-right py-0.5">
              <div className="w-[30%] text-left border-r border-black px-1">घरपट्टी</div>
              <div className="w-[23.3%] border-r border-black px-1">{hPre || '-'}</div>
              <div className="w-[23.3%] border-r border-black px-1">{hCur}</div>
              <div className="w-[23.4%] px-1">{hTot}</div>
            </div>
            {/* Light Tax */}
            <div className="flex text-right py-0.5">
              <div className="w-[30%] text-left border-r border-black px-1">विजकर</div>
              <div className="w-[23.3%] border-r border-black px-1">{lPre || '-'}</div>
              <div className="w-[23.3%] border-r border-black px-1">{lCur}</div>
              <div className="w-[23.4%] px-1">{lTot}</div>
            </div>
            {/* Health Tax */}
            <div className="flex text-right py-0.5">
              <div className="w-[30%] text-left border-r border-black px-1">आरोग्य कर</div>
              <div className="w-[23.3%] border-r border-black px-1">{hlPre || '-'}</div>
              <div className="w-[23.3%] border-r border-black px-1">{hlCur}</div>
              <div className="w-[23.4%] px-1">{hlTot}</div>
            </div>
            {/* Notice Fee */}
            <div className="flex text-right py-0.5">
              <div className="w-[30%] text-left border-r border-black px-1">नोटिस फी</div>
              <div className="w-[23.3%] border-r border-black px-1">-</div>
              <div className="w-[23.3%] border-r border-black px-1">{noticeFee || '-'}</div>
              <div className="w-[23.4%] px-1">{noticeFee || '-'}</div>
            </div>
            {/* Warrant Fee */}
            <div className="flex text-right py-0.5">
              <div className="w-[30%] text-left border-r border-black px-1">वॉरंट फी</div>
              <div className="w-[23.3%] border-r border-black px-1">-</div>
              <div className="w-[23.3%] border-r border-black px-1">{warrantFee || '-'}</div>
              <div className="w-[23.4%] px-1">{warrantFee || '-'}</div>
            </div>
            {/* Penalty */}
            <div className="flex text-right py-0.5">
              <div className="w-[30%] text-left border-r border-black px-1 text-[11px] leading-tight">घरपट्टी थकबाकी दंड</div>
              <div className="w-[23.3%] border-r border-black px-1"></div>
              <div className="w-[23.3%] border-r border-black px-1"></div>
              <div className="w-[23.4%] px-1">{penalty}</div>
            </div>

            {/* Empty Spacer to push Total down if needed, but flex-grow handles it */}
            <div className="h-4 border-r-[0.5px] border-black ml-[30%]"></div>
          </div>

          {/* Total Row */}
          <div className="flex border-t border-black font-bold text-sm border-b border-black">
            <div className="w-[30%] text-left border-r border-black px-1">एकुण</div>
            <div className="w-[23.3%] text-right border-r border-black px-1">{totalArrears}</div>
            <div className="w-[23.3%] text-right border-r border-black px-1">{totalCurrent}</div>
            <div className="w-[23.4%] text-right px-1">{subTotal}</div>
          </div>

          {/* Discount Row */}
          <div className="flex border-b border-black text-xs font-semibold">
            <div className="w-[76.6%] text-right border-r border-black px-1 py-1">
              दि.{discountDate} च्या आत संपूर्ण कर एकरकमी भरल्यास करातुन सुट
            </div>
            <div className="w-[23.4%] text-right px-1 py-1">{discount}</div>
          </div>

          {/* Final Total */}
          <div className="flex text-sm font-bold">
            <div className="w-[76.6%] text-right border-r border-black px-1 py-1">एकुण</div>
            <div className="w-[23.4%] text-right px-1 py-1">{finalTotal}</div>
          </div>
        </div>

        {/* Right Side: Instructions */}
        <div className="w-[35%] flex flex-col justify-between">
          {/* Header Bits */}
          <div className="p-1 flex justify-between text-xs font-bold border-b border-transparent">
            <span>मालमत्ता क्र. : {record.propertyNo}</span>
            <span>बद्दल</span>
          </div>

          {/* Text Body */}
          <div className="p-2 text-[11px] leading-snug text-justify font-serif">
            <p>
              येथील ग्रामपंचायत यांजकडुन कळविण्यात येते की, आपणाकडे सदरहु ग्रामपंचायतची बाजुस दिलेल्या तपशिलाप्रमाणे रक्कम येणे निघत आहे.
              ती या बिलावरुन मागितली जात आहे तरी तुम्ही सदरहु रक्कम पंधरा दिवसाच्या आत म्हणजे
            </p>
            <div className="font-bold text-center my-1 tracking-widest bg-gray-100 py-0.5 border border-gray-300">
              {dueDate}
            </div>
            <p>
              भरणा करुन त्याची लेखी पावती घ्यावी. त्याप्रमाणे तुमच्याकडुन झाले नाही तर म. १९५८ चा ग्राम.का.क्र. १२९ (२) नियम क्र. (३) प्रमाणे तुम्हास नोटीस केली जाईल व पुढे जरु तर कायदेशीर कारवाई केली जाईल. कळावे.
            </p>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="border-t border-black">
        {/* Amount in words */}
        <div className="p-1 border-b border-black font-bold text-sm">
          अक्षरी रु. {numToMarathiWords(finalTotal)} रुपये फक्त
        </div>

        {/* Warning */}
        <div className="text-center p-1 text-[11px] font-bold">
          बिलातील देय रक्कम पुर्ण भरल्याशिवाय कोणताही दाखला उतारा मिळणार नाही. आदेशावरुन
        </div>

        {/* Signatures & Date */}
        <div className="flex justify-between items-end px-2 pb-2 mt-2">
          <div className="text-xs">
            <p className="font-bold">दिनांक : {billDate}</p>
            <p className="mt-6 font-bold">या बिलाची पोच स्थळ प्रतीवर घ्यावी.</p>
          </div>
          <div className="text-center text-xs">
            <p className="mb-8 font-bold">ग्रामपंचायत अधिकारी</p>
          </div>
          <div className="text-center text-xs">
            <p className="mb-8 font-bold">सरपंच</p>
          </div>
        </div>
      </div>

      {/* Bottom Bank Line */}
      <div className="text-[10px] font-bold px-2 py-0.5 border-t border-black">
        To pay Online Use AcNo IFSCCode Name Bank Branch
      </div>
    </div>
  );

  return (
    <div id="printable-content" className="bg-white min-h-screen p-4 text-black">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          body {
            -webkit-print-color-adjust: exact;
          }
          #printable-content, #printable-content * {
            visibility: visible;
          }
          #printable-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          @page { size: portrait; margin: 5mm; }
          .no-print { display: none !important; }
          .page-break { page-break-before: always; }
        }
      `}</style>

      {/* Print Button */}
      <div className="max-w-[210mm] mx-auto mb-4 no-print flex justify-end">
        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-bold"
        >
          Print / Save PDF
        </button>
      </div>

      <div className="w-fit mx-auto">
        <BillTemplate copyTitle="Original" />

        {/* Cut Line - Hidden in Print */}
        <div className="border-t-2 border-dashed border-gray-400 my-4 relative h-4 flex items-center justify-center no-print">
          <span className="bg-white px-2 text-xs text-gray-500 absolute">Cut Here ✂️</span>
        </div>

        <div className="page-break">
          <BillTemplate copyTitle="Duplicate" />
        </div>
      </div>
    </div>
  );
};

export default TaxBillReport;
